package com.evoapps.evoshift.domain.usecase.requests

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase

/**
 * SH-164: an approval could not be undone because the request changed since (a colleague took the
 * shift from the marketplace, another manager decided it again).
 */
class DecisionChangedException(message: String) : IllegalStateException(message)

/**
 * SH-164: the Undo of an absence decision made in one tap: the request is PENDING again, as the
 * employee sent it ([before] is the request as it was listed, before the decision).
 */
class UndoAbsenceDecisionUseCase(private val absenceRepository: AbsenceRepository) {
    suspend operator fun invoke(before: Absence): Result<Unit> {
        if (before.status != AbsenceStatus.PENDING) return Result.failure(IllegalArgumentException("Only a pending request can be put back"))
        return absenceRepository.updateAbsence(before.copy(processedBy = null, processedAt = null, rejectionReason = null))
    }
}

/**
 * SH-164: the Undo of a swap approval. [before] is the shift as it waited for the decision:
 * - a claim (TAKEN_PENDING): the shift goes back to the person who gave it away (or is open
 *   again) and the claim waits for a decision again;
 * - a give-away (REQUESTED_BY_OWNER): it leaves the marketplace and waits for a decision again.
 *
 * Only while the shift is still as the approval left it; otherwise [DecisionChangedException]
 * (nothing is overwritten). The cost snapshot is priced again for the person back on the shift.
 * The approval's notifications are not taken back.
 */
class UndoSwapApprovalUseCase(
    private val shiftRepository: ShiftRepository,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    suspend operator fun invoke(before: Shift): Result<Unit> = runCatching {
        val request = before.swapRequest ?: throw IllegalArgumentException("No request to put back")
        val current = shiftRepository.getShift(before.id).getOrThrow()
        val unchanged = when (request.status) {
            SwapStatus.TAKEN_PENDING -> current.swapRequest == null && current.userId == request.candidateUserId
            SwapStatus.REQUESTED_BY_OWNER -> current.swapRequest?.status == SwapStatus.AVAILABLE &&
                current.swapRequest?.candidateUserId == null && current.userId == before.userId
            else -> throw IllegalArgumentException("Not an approval: ${request.status}")
        }
        if (!unchanged) throw DecisionChangedException("Shift ${before.id} changed after the approval")
        shiftRepository.restoreSwap(before.id, before.userId, before.isOpen, before.conflictMetadata, request).getOrThrow()
        if (request.status == SwapStatus.TAKEN_PENDING) calculateShiftCost?.invoke(before.id)
    }
}
